import React from "react";
import { useSelector, useDispatch } from "react-redux";
import { Divider, Stack, TextField, Typography, useTheme } from "@mui/material";

import { useFormik } from "formik";
import * as yup from "yup";

import * as selectors from "selectors";
import actions from "actions";
import { useIntake } from "components/SelfService/IntakeProvider";
import { STAGES } from "components/SelfService/utils";
import { useGlobalToast } from "components/GlobalToastProvider";
import { updateCaregiverInfo, updateClientPersonalInfo } from "api/customers";
import { LoadingButton } from "@mui/lab";
import { formatToPhoneNumber } from "@answersnow/pkg-theme/utils";

const validationSchema = yup.object({
  firstName: yup.string().required("Required"),
  lastName: yup.string().required("Required"),
  phoneNumber: yup.string().required("Required"),
  childFirstName: yup.string().required("Required"),
  childLastName: yup.string().required("Required"),
});

export default function ConfirmAccountDetails({ ...rest }) {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { showToast } = useGlobalToast();
  const { clientId, setCurrentStage, saveProgress } = useIntake();
  const customerDetails = useSelector(selectors.getCustomerDetails);
  const { details } = customerDetails;

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      firstName: details?.first_name || "",
      lastName: details?.last_name || "",
      email: details?.email || "",
      phoneNumber: details?.phone_number || "",
      childFirstName: details?.name || "",
      childLastName: details?.child_last_name || "",
    },
    validationSchema,
    onSubmit: handleSave,
  });

  const handlePhoneNumberChange = (e) => {
    const phone = e.target.value.replace(/[^\d]/g, "");
    formik.setFieldValue("phoneNumber", phone);
  };

  async function handleSave(values) {
    try {
      const userId = details?.userid;

      await updateCaregiverInfo({
        userId,
        data: {
          firstName: values.firstName,
          lastName: values.lastName,
          phoneNumber: values.phoneNumber,
        },
      });

      await updateClientPersonalInfo({
        clientId,
        data: {
          firstName: values.childFirstName,
          lastName: values.childLastName,
          preferredFirstName: details?.preferred_first_name || "",
          dob: details?.dob || null,
          gender: details?.gender || "",
          identifiedGender: details?.identified_gender || "",
          transgender: details?.transgender || false,
          pronouns: details?.pronouns || "",
          livesWith: details?.lives_with || "",
          ethnicity: details?.ethnicity || "",
          primaryLanguage: details?.primary_language || "",
          needsInterpreter: details?.needs_interpreter || false,
          preAuthRequired: details?.pre_auth_required || false,
          therapeuticConsultation: details?.therapeutic_consultation || false,
          requiresTablet: details?.requires_tablet || false,
          location: {
            line1: details?.line1 || "",
            line2: details?.line2 || "",
            state: details?.state || "",
            city: details?.city || "",
            zip: details?.zip || "",
            country: details?.country || "US",
          },
          clientTimezone: details?.timezone || details?.timezone_jhi || "",
        },
      });

      saveProgress({
        stage: "profileInformation",
        subStage: "profileInformation",
        isStageCompleted: true,
        isSubStageCompleted: true,
        subStageData: values,
        onError: () => {
          showToast({
            message: "Account details were saved, but we couldn't save intake progress",
            errorState: true,
          });
        },
        onSettled: () => setCurrentStage(STAGES.insuranceCard.name),
      });
      dispatch(actions.loadCustomerDetails(clientId));
    } catch (err) {
      showToast({
        message: "Failed to save account details",
        errorState: true,
        retryHandler: () => formik.handleSubmit(),
      });
    }
  }

  return (
    <Stack gap={theme.spacing(7)} {...rest}>
      <Typography variant="h3">Confirm account details</Typography>
      <Stack gap={theme.spacing(7)}>
        <TextField
          label="First Name"
          name="firstName"
          value={formik.values.firstName}
          onChange={formik.handleChange}
          error={formik.touched.firstName && Boolean(formik.errors.firstName)}
          helperText={formik.touched.firstName && formik.errors.firstName}
        />
        <TextField
          label="Last Name"
          name="lastName"
          value={formik.values.lastName}
          onChange={formik.handleChange}
          error={formik.touched.lastName && Boolean(formik.errors.lastName)}
          helperText={formik.touched.lastName && formik.errors.lastName}
        />
        <TextField label="Email Address" name="email" value={formik.values.email} disabled />
        <TextField
          label="Phone Number"
          name="phoneNumber"
          value={formatToPhoneNumber(formik.values.phoneNumber)}
          onChange={handlePhoneNumberChange}
          error={formik.touched.phoneNumber && Boolean(formik.errors.phoneNumber)}
          helperText={formik.touched.phoneNumber && formik.errors.phoneNumber}
        />

        <Divider />

        <TextField
          label="Child's First Name"
          name="childFirstName"
          value={formik.values.childFirstName}
          onChange={formik.handleChange}
          error={formik.touched.childFirstName && Boolean(formik.errors.childFirstName)}
          helperText={formik.touched.childFirstName && formik.errors.childFirstName}
        />
        <TextField
          label="Child's Last Name"
          name="childLastName"
          value={formik.values.childLastName}
          onChange={formik.handleChange}
          error={formik.touched.childLastName && Boolean(formik.errors.childLastName)}
          helperText={formik.touched.childLastName && formik.errors.childLastName}
        />

        <LoadingButton
          size="medium"
          disabled={!formik.isValid}
          onClick={formik.handleSubmit}
          loading={formik.isSubmitting}
        >
          Continue
        </LoadingButton>
      </Stack>
    </Stack>
  );
}
